import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  FormControlLabel,
  Radio,
  Switch,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toast } from 'react-toastify';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventBusy as EventBusyIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import { colors, getTableRowBackground, getTableHeaderStyles, getActualTableRowBackground } from "../theme/colors";
import * as exceptionService from "../services/exceptionService";
import tenantService from "../services/tenantService";
import tenantUsersService from "../services/tenantUsersService";
import Pagination from "../components/Pagination";
import { getUserTypeFromConnections, isSuperAdmin } from "../utils/userHelper";

const Exceptions = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [exceptions, setExceptions] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [currentTenant, setCurrentTenant] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingException, setEditingException] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [pendingExceptionData, setPendingExceptionData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "one_day", // "one_day", "multiple_days", "specific_hours_one_day", "specific_hours_multiple_days", "all_days_specific_hours"
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    isRecurring: false,
    recurringDays: [], // ["monday", "tuesday", ...]
    recurringEndDate: "",
    forSpecificEmployee: false,
    employeeId: "",
  });

  // Helper funkcija za dobijanje tenantId iz tenantConnections
  const getCurrentTenantId = useCallback(() => {
    // Ako postoji tenantId direktno na user objektu (stari način)
    if (currentUser?.tenantId) {
      return currentUser.tenantId?._id || currentUser.tenantId;
    }
    
    // Ako postoji tenantConnections, uzmi tenantId iz prve konekcije
    if (currentUser?.tenantConnections && currentUser.tenantConnections.length > 0) {
      const firstConnection = currentUser.tenantConnections[0];
      return firstConnection?.tenantId?._id || firstConnection?.tenantId;
    }
    
    return null;
  }, [currentUser]);

  // Helper funkcija za čitanje userType iz tenantConnections

  const fetchTenants = useCallback(async () => {
    try {
      const response = await tenantService.getAllTenants();
      const tenantsData = response.tenants || response || [];
      setTenants(tenantsData);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  }, []);

  const fetchCurrentTenant = useCallback(async (tenantId) => {
    try {
      const response = await tenantService.getTenantById(tenantId);
      setCurrentTenant(response);
    } catch (err) {
      console.error('Error fetching current tenant:', err);
      setCurrentTenant(null);
    }
  }, []);

  // Generiši vremenske slotove na osnovu radnih sati tenanta
  const generateTimeSlots = useCallback(() => {
    // Pokušaj da koristiš currentTenant, ako nema onda currentUser.tenantId
    const tenantId = getCurrentTenantId();
    const tenantData = currentTenant || (tenantId ? { tenantId } : null);
    
    if (!tenantData?.workingHours) {
      // Fallback na default slotove ako nema radnih sati
      return Array.from({ length: 24 }, (_, hour) => 
        Array.from({ length: 2 }, (_, minute) => {
          const time = `${hour.toString().padStart(2, '0')}:${(minute * 30).toString().padStart(2, '0')}`;
          return time;
        })
      ).flat();
    }

    const workingHours = tenantData.workingHours;
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    let earliestTime = '23:59';
    let latestTime = '00:00';

    // Pronađi najranije i najkasnije vreme iz svih radnih dana
    days.forEach(day => {
      const dayHours = workingHours[day];
      if (dayHours && dayHours.isOpen) {
        // Proveri open vreme
        if (dayHours.open && dayHours.open < earliestTime) {
          earliestTime = dayHours.open;
        }
        // Proveri close vreme
        if (dayHours.close && dayHours.close > latestTime) {
          latestTime = dayHours.close;
        }
      }
    });

    // Generiši slotove od najranijeg do najkasnijeg vremena
    const slots = [];
    const [startHour, startMinute] = earliestTime.split(':').map(Number);
    const [endHour, endMinute] = latestTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    for (let minutes = startMinutes; minutes <= endMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(time);
    }

    return slots;
  }, [currentTenant, getCurrentTenantId]);

  // Generiši slotove za početak (bez poslednjeg sata)
  const generateStartTimeSlots = useCallback(() => {
    const allSlots = generateTimeSlots();
    return allSlots.slice(0, -1); // Ukloni poslednji slot
  }, [generateTimeSlots]);

  // Generiši slotove za kraj (bez najranijeg sata)
  const generateEndTimeSlots = useCallback(() => {
    const allSlots = generateTimeSlots();
    return allSlots.slice(1); // Ukloni prvi slot
  }, [generateTimeSlots]);



  useEffect(() => {
    if (isSuperAdmin(currentUser)) {
      fetchTenants();
    } else {
      const tenantId = getCurrentTenantId();
      
      if (tenantId) {
        setSelectedTenantId(tenantId);
      }
    }
  }, [currentUser, fetchTenants, getCurrentTenantId]);

  // Fetch podataka o tenant-u kada se promeni selectedTenantId
  useEffect(() => {
    if (selectedTenantId) {
      fetchCurrentTenant(selectedTenantId);
    } else {
      setCurrentTenant(null);
    }
  }, [selectedTenantId, fetchCurrentTenant]);

  // Učitaj zaposlene i tenant admine za izabranog tenant-a
  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (!selectedTenantId) {
          setUsers([]);
          return;
        }
        
        // Koristi tenantUsersService umesto userService.getUsersByTenant jer vraća korisnike preko tenantConnections
        // Za Exceptions treba samo zaposleni i tenant_admin, ne korisnici
        const res = await tenantUsersService.getTenantUsers({
          tenantId: selectedTenantId,
          page: 1,
          limit: 1000,
          isEmployeesPage: true, // Za Exceptions treba samo zaposleni
        });
        const list = res.users || res?.data?.users || res || [];
        
        if (!Array.isArray(list) || list.length === 0) {
          setUsers([]);
          return;
        }
        
        // Filtriraj na employee, tenant_admin i super_admin koristeći helper funkciju
        const filtered = list.filter(u => {
          if (!u || !u._id) {
            return false;
          }
          
          // Super admin uvek može biti izabran
          if (isSuperAdmin(u)) {
            return true;
          }
          
          // Za ostale korisnike, prvo proveri userType iz tenantConnections
          const userActualType = getUserTypeFromConnections(u, selectedTenantId);
          
          // Ako getUserTypeFromConnections ne vraća ništa, probaj direktan userType
          const finalUserType = userActualType || u.userType;
          
          return finalUserType === 'employee' || finalUserType === 'tenant_admin';
        });
        
        setUsers(filtered);
      } catch (e) {
        console.error('Error loading users for exceptions:', e);
        setUsers([]);
      }
    };
    loadUsers();
  }, [selectedTenantId]);

  // Centralizovana funkcija za učitavanje exceptions
  const fetchExceptions = useCallback(async () => {
    if (!selectedTenantId) {
      setExceptions([]);
      setTotalCount(0);
      return;
    }

        try {
          setError(null);
          
          // Osiguravamo da je tenantId string
          const tenantId = typeof selectedTenantId === 'object' 
            ? selectedTenantId._id 
            : selectedTenantId;
          
          const response = await exceptionService.getAllExceptions(tenantId, { 
            isActive: true, 
            page: page + 1, 
            limit: rowsPerPage 
          });
          
          // Backend vraća podatke u response.data
          const data = response.data || response;
          const exceptionsToSet = data.exceptions || data;
          setExceptions(Array.isArray(exceptionsToSet) ? exceptionsToSet : []);
          
          // Postavi total count
          const total = parseInt(data.totalItems) || parseInt(data.total) || (data.exceptions ? data.exceptions.length : 0);
          setTotalCount(total);
          
          // Sinhronizuj page sa backend-om
          const backendPage = parseInt(data.currentPage) - 1;
          if (backendPage !== page && Math.abs(backendPage - page) > 0) {
            setPage(backendPage);
          }
        } catch (err) {
          console.error('Error fetching exceptions:', err);
          if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Backend server nije dostupan. Proverite da li je server pokrenut.');
          } else {
            setError(err.response?.data?.message || err.message || 'Greška pri učitavanju neradnog vremena');
          }
        }
  }, [selectedTenantId, page, rowsPerPage]);

  // Reset page when selectedTenantId changes
  useEffect(() => {
    setPage(0);
  }, [selectedTenantId]);

  // Učitaj exceptions kada se promene zavisnosti
  useEffect(() => {
    fetchExceptions();
  }, [fetchExceptions]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = useCallback((exception = null) => {
    // Resetuj opcije za određenog zaposlenog ako ima samo jedan zaposleni
    const hasMultipleEmployees = users.length > 1;
    
    if (exception) {
      setEditingException(exception);
      setFormData({
        title: exception.title || "",
        description: exception.description || "",
        type: exception.type || "one_day",
        startDate: exception.formattedStartDate || exception.startDate?.split('T')[0] || "",
        endDate: exception.formattedEndDate || exception.endDate?.split('T')[0] || "",
        startTime: exception.startTime || "",
        endTime: exception.endTime || "",
        isRecurring: exception.isRecurring || false,
        recurringDays: exception.recurringDays || [],
        recurringEndDate: exception.recurringEndDate?.split('T')[0] || "",
        forSpecificEmployee: hasMultipleEmployees ? (exception.employeeId ? true : false) : false,
        employeeId: hasMultipleEmployees ? (exception.employeeId?._id || exception.employeeId || "") : "",
      });
    } else {
      setEditingException(null);
      setFormData({
        title: "",
        description: "",
        type: "one_day",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        isRecurring: false,
        recurringDays: [],
        recurringEndDate: "",
        forSpecificEmployee: false,
        employeeId: "",
      });
    }
    setOpenDialog(true);
  }, [users.length]);

  // Event listener za navbar dugme
  useEffect(() => {
    const handleOpenExceptionDialog = () => {
      handleOpenDialog();
    };

    window.addEventListener('openExceptionDialog', handleOpenExceptionDialog);
    
    return () => {
      window.removeEventListener('openExceptionDialog', handleOpenExceptionDialog);
    };
  }, [handleOpenDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingException(null);
    setFormData({
      title: "",
      description: "",
      type: "one_day",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      isRecurring: false,
      recurringDays: [],
      recurringEndDate: "",
    });
  };

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      setError(null);
      
      // Osiguravamo da je tenantId string za proveru
      const tenantId = typeof selectedTenantId === 'object' 
        ? selectedTenantId._id 
        : selectedTenantId;

      if (!tenantId) {
        toast.error("Molimo odaberite klijenta");
        return;
      }

      if (!formData.title.trim()) {
        toast.error("Naslov je obavezan");
        return;
      }

      if (formData.type !== "all_days_specific_hours" && !formData.startDate) {
        toast.error("Datum početka je obavezan");
        return;
      }

      if ((formData.type === "multiple_days" || formData.type === "specific_hours_multiple_days") && !formData.endDate) {
        toast.error("Datum kraja je obavezan za tipove koji traju više dana");
        return;
      }

      if ((formData.type === "specific_hours_one_day" || formData.type === "specific_hours_multiple_days" || formData.type === "all_days_specific_hours") && (!formData.startTime || !formData.endTime)) {
        toast.error("Vreme početka i kraja je obavezno za specifične sate neradnog vremena");
        return;
      }


             const exceptionData = {
         title: formData.title.trim(),
         description: formData.description.trim(),
         type: formData.type,
         // startDate se ne šalje za all_days_specific_hours tip
         ...(formData.type !== "all_days_specific_hours" ? {
           startDate: formData.startDate,
           // endDate se uvek šalje - za one_day tipove se postavlja na startDate
           endDate: (formData.type === "one_day" || formData.type === "specific_hours_one_day") 
             ? formData.startDate 
             : (formData.endDate || null)
         } : {}),
         // startTime i endTime se šalju za specific_hours tipove i all_days_specific_hours
         ...(formData.type === "specific_hours_one_day" || formData.type === "specific_hours_multiple_days" || formData.type === "all_days_specific_hours" ? { 
           startTime: formData.startTime || null, 
           endTime: formData.endTime || null 
         } : {}),
         isRecurring: formData.isRecurring || formData.type === "all_days_specific_hours",
         recurringDays: (formData.isRecurring || formData.type === "all_days_specific_hours") ? formData.recurringDays : [],
         // recurringEndDate se ne šalje za all_days_specific_hours jer se uvek ponavlja
         recurringEndDate: formData.type === "all_days_specific_hours" ? null : (formData.isRecurring ? formData.recurringEndDate : null),
        tenantId: tenantId,
        ...(formData.forSpecificEmployee && formData.employeeId ? { employeeId: formData.employeeId } : {})
       };

      if (editingException) {
        // Update
        const response = await exceptionService.updateException(editingException._id, exceptionData);
        if (response && response._id) {
          toast.success("Neradno vreme je uspešno ažurirano!");
          handleCloseDialog();
          // Ponovno učitaj podatke
          await fetchExceptions();
                 } else {
           toast.error('Greška pri ažuriranju neradnog vremena');
         }
      } else {
        // Create - direktno pokušaj kreiranje, hvataj 409 grešku
        try {
          const response = await exceptionService.createException(exceptionData);
          if (response && response._id) {
            toast.success("Neradno vreme je uspešno kreirano!");
            handleCloseDialog();
            // Ponovno učitaj podatke
            await fetchExceptions();
          } else {
            toast.error('Greška pri kreiranju neradnog vremena');
          }
        } catch (createErr) {
          // Proveri da li je 409 greška (konflikti)
          if (createErr.response && createErr.response.status === 409) {
            const conflictData = createErr.response.data.conflicts || createErr.response.data;
            showConfirmationDialog(conflictData, exceptionData);
          } else {
            // Ostale greške
            console.error('Error creating exception:', createErr);
            toast.error(createErr.response?.data?.message || createErr.message || 'Greška pri kreiranju neradnog vremena');
          }
        }
      }
    } catch (err) {
      console.error('Error submitting exception:', err);
      if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
        toast.error('Backend server nije dostupan. Proverite da li je server pokrenut.');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Greška pri čuvanju neradnog vremena');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
         if (window.confirm("Da li ste sigurni da želite da obrišete ovo neradno vreme?")) {
      try {
        setError(null);
        await exceptionService.deleteException(id);
          toast.success("Neradno vreme je uspešno obrisano!");
        // Ponovno učitaj podatke
        await fetchExceptions();
      } catch (err) {
        console.error('Error deleting exception:', err);
        if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
          toast.error('Backend server nije dostupan. Proverite da li je server pokrenut.');
        } else {
          toast.error(err.response?.data?.message || err.message || 'Greška pri brisanju neradnog vremena');
        }
      }
    }
  };

  const handleRecurringDayChange = (day) => {
    setFormData(prev => ({
      ...prev,
      recurringDays: prev.recurringDays.includes(day)
        ? prev.recurringDays.filter(d => d !== day)
        : [...prev.recurringDays, day]
    }));
  };


  // Funkcija za kreiranje sa potvrdom
  const createWithConfirmation = async () => {
    if (!pendingExceptionData) {
      toast.error('Nema podataka za kreiranje');
      return;
    }
    
    try {
      const result = await exceptionService.createExceptionWithConfirmation(pendingExceptionData);
      
      if (result && result.exception && result.exception._id) {
        toast.success(`Neradno vreme kreirano. Otkazano je ${result.cancelledCount || 0} termina.`);
        handleCloseDialog();
        setShowConflictDialog(false);
        setConflictData(null);
        setPendingExceptionData(null);
        
        // Ponovno učitaj podatke
        await fetchExceptions();
      } else {
        toast.error('Greška pri kreiranju neradnog vremena');
      }
    } catch (err) {
      console.error('Error creating with confirmation:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri kreiranju neradnog vremena');
    }
  };

  // Funkcija za prikazivanje potvrde
  const showConfirmationDialog = (conflictData, exceptionData) => {
    setConflictData(conflictData);
    setPendingExceptionData(exceptionData);
    setShowConflictDialog(true);
  };

  const getDayName = (day) => {
    const days = {
      monday: "Ponedeljak",
      tuesday: "Utorak", 
      wednesday: "Sreda",
      thursday: "Četvrtak",
      friday: "Petak",
      saturday: "Subota",
      sunday: "Nedelja"
    };
    return days[day] || day;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('sr-RS');
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString;
  };



  return (
    <>

             {error && (
         <Alert severity="error" sx={{ mb: 2, ...commonStyles.alert('error') }}>
           {error}
         </Alert>
       )}

       {isSuperAdmin(currentUser) && (
         <Box sx={{ mb: 3 }}>
           <FormControl fullWidth sx={{ ...commonStyles.formField, maxWidth: 400 }}>
             <InputLabel sx={commonStyles.textSecondary}>Odaberite klijenta</InputLabel>
             <Select
               value={selectedTenantId}
               label="Odaberite klijenta"
                               onChange={(e) => {
                  setSelectedTenantId(e.target.value);
                }}
               sx={commonStyles.selectField}
               MenuProps={{
                 PaperProps: {
                   sx: commonStyles.selectMenu
                 }
               }}
             >
               <MenuItem value="">
                 <em>Odaberite klijenta</em>
               </MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </MenuItem>
                ))}
             </Select>
           </FormControl>
           {!selectedTenantId && (
             <Alert severity="info" sx={{ mt: 2, ...commonStyles.alert('info') }}>
               Molimo odaberite klijenta da vidite neradno vreme
             </Alert>
           )}
         </Box>
       )}

        
        {/* Desktop dugme - iznad tabele */}
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={!selectedTenantId}
              sx={commonStyles.gradientButton}
            >
              Dodaj neradno vreme
            </Button>
          </Box>
        )}

        {/* Responsive prikaz - tabela za desktop, kartice za mobilne */}
        {isMobile ? (
          // Mobilni prikaz - kartice
          <>
            {exceptions.map((exception, index) => (
              <Card key={exception._id} sx={{
                ...commonStyles.paper,
                backgroundColor: getTableRowBackground(index, isDarkMode),
                border: `1px solid ${customColors.border.main}`,
                borderRadius: 2,
                position: 'relative',
                overflow: 'visible',
                mb: 1.5
              }}>
                <CardContent sx={{ 
                  p: 1.5, 
                  '&:last-child': { pb: 1.5 },
                  position: 'relative'
                }}>
                  {/* Header sa naslovom i akcijama */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between',
                    mb: 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <EventBusyIcon sx={{ mr: 1, color: customColors.primary.main, fontSize: 20 }} />
                      <Typography variant="h6" sx={{ 
                        ...commonStyles.textPrimary,
                        fontSize: '1.1rem',
                        fontWeight: 600
                      }}>
                        {exception.title}
                      </Typography>
                    </Box>
                    
                    {/* Akcije */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(exception)}
                        sx={commonStyles.iconButton(customColors.primary.main)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(exception._id)}
                        sx={commonStyles.iconButton(colors.actions.delete)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Opis */}
                  <Typography variant="body2" sx={{ 
                    ...commonStyles.textSecondary, 
                    mb: 1.5,
                    fontSize: '0.9rem'
                  }}>
                    {exception.description}
                  </Typography>

                  {/* Informacije u grid layout */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    mb: 1
                  }}>
                    {/* Tip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.75rem'
                      }}>
                        Tip:
                      </Typography>
                      <Chip
                        label={
                          exception.type === "one_day" ? "Jedan dan" :
                          exception.type === "multiple_days" ? "Više dana" :
                          exception.type === "specific_hours_one_day" ? "Specifični sati" :
                          exception.type === "specific_hours_multiple_days" ? "Specifični sati" :
                          exception.type === "all_days_specific_hours" ? "Pauza - svi dani" :
                          "Nepoznat tip"
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          color: customColors.primary.main,
                          borderColor: customColors.primary.main,
                          background: 'transparent',
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>

                    {/* Period - sakrij za all_days_specific_hours */}
                    {exception.type !== "all_days_specific_hours" && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ 
                          ...commonStyles.textSecondary,
                          fontSize: '0.75rem'
                        }}>
                          Period:
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          ...commonStyles.textPrimary,
                          fontSize: '0.85rem'
                        }}>
                          {formatDate(exception.startDate)}
                          {exception.startDate !== exception.endDate && ` - ${formatDate(exception.endDate)}`}
                        </Typography>
                      </Box>
                    )}

                    {/* Vreme */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.75rem'
                      }}>
                        Vreme:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        ...commonStyles.textPrimary,
                        fontSize: '0.85rem'
                      }}>
                        {(exception.type === "specific_hours_one_day" || exception.type === "specific_hours_multiple_days" || exception.type === "all_days_specific_hours")
                          ? `${formatTime(exception.startTime)} - ${formatTime(exception.endTime)}`
                          : "Ceo dan"
                        }
                      </Typography>
                    </Box>

                    {/* Ponavljanje */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.75rem'
                      }}>
                        Ponavljanje:
                      </Typography>
                      {exception.isRecurring ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label="Da"
                            size="small"
                            color="secondary"
                            sx={{ 
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                          <Typography variant="caption" sx={{ 
                            ...commonStyles.textSecondary,
                            fontSize: '0.7rem'
                          }}>
                            {exception.recurringDays.map(day => getDayName(day)).join(", ")}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip
                          label="Ne"
                          size="small"
                          color="default"
                          sx={{
                            background: colors.status.inactive.background,
                            border: colors.status.inactive.border,
                            color: colors.status.inactive.light,
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Footer sa kreirao i datum */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 1,
                    borderTop: `1px solid ${customColors.border.light}`
                  }}>
                    <Typography variant="caption" sx={{ 
                      ...commonStyles.textSecondary,
                      fontSize: '0.7rem'
                    }}>
                      Kreirao: {exception.createdBy ? 
                        `${exception.createdBy.firstName || ''} ${exception.createdBy.lastName || ''}`.trim() || 
                        exception.createdBy.email || 
                        'Nepoznato' 
                        : 'Nepoznato'
                      }
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      ...commonStyles.textSecondary,
                      fontSize: '0.7rem'
                    }}>
                      {exception.createdAt ? formatDate(exception.createdAt) : 'Nepoznato'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <div>
            {/* Desktop prikaz - tabela */}
            <TableContainer sx={{ backgroundColor: isDarkMode ? 'transparent' : commonStyles.tableContainer.backgroundColor }}>
              <Table>
                <TableHead>
                  <TableRow sx={{
                    background: colors.table.header.background,
                    '& .MuiTableCell-head': {
                      color: commonStyles.textPrimary.color,
                      background: getTableHeaderStyles(isDarkMode).cellBackground,
                      fontWeight: 600,
                      borderBottom: `1px solid ${customColors.border.light}`
                    }
                  }}>
                    <TableCell>Naslov</TableCell>
                    <TableCell>Opis</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Vreme</TableCell>
                    {users.length > 1 && <TableCell>Zaposleni</TableCell>}
                    <TableCell>Ponavljanje</TableCell>
                    <TableCell>Kreirao</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell>Akcije</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exceptions.map((exception, index) => (
                    <TableRow key={exception._id} sx={{
                      backgroundColor: getActualTableRowBackground(index, isDarkMode),
                      '& .MuiTableCell-body': {
                        borderBottom: `1px solid ${customColors.border.light}`,
                        color: commonStyles.textPrimary.color
                      }
                    }}>
                      <TableCell sx={commonStyles.textPrimary}>
                        <Box display="flex" alignItems="center">
                          <EventBusyIcon sx={{ mr: 1, color: customColors.primary.main }} />
                          {exception.title}
                        </Box>
                      </TableCell>
                      <TableCell sx={commonStyles.textPrimary}>{exception.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            exception.type === "one_day" ? "Jedan dan" :
                            exception.type === "multiple_days" ? "Više dana" :
                            exception.type === "specific_hours_one_day" ? "Specifični sati - jedan dan" :
                            exception.type === "specific_hours_multiple_days" ? "Specifični sati - više dana" :
                            exception.type === "all_days_specific_hours" ? "Pauza - svi dani" :
                            "Nepoznat tip"
                          }
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            color: customColors.primary.main,
                            borderColor: customColors.primary.main,
                            background: 'transparent',
                            '&:hover': {
                              background: `${customColors.primary.main}20`,
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={commonStyles.textPrimary}>
                        {exception.type === "all_days_specific_hours" ? "Uvek" : (
                          <>
                            {formatDate(exception.startDate)}
                            {exception.startDate !== exception.endDate && ` - ${formatDate(exception.endDate)}`}
                          </>
                        )}
                      </TableCell>
                      <TableCell sx={commonStyles.textPrimary}>
                        {(exception.type === "specific_hours_one_day" || exception.type === "specific_hours_multiple_days" || exception.type === "all_days_specific_hours")
                          ? `${formatTime(exception.startTime)} - ${formatTime(exception.endTime)}`
                          : "Ceo dan"
                        }
                      </TableCell>
                      {users.length > 1 && (
                        <TableCell sx={commonStyles.textPrimary}>
                          {(() => {
                            const emp = exception.employeeId;
                            if (!emp) return '—';
                            if (typeof emp === 'object') {
                              const full = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                              return full || emp.email || '—';
                            }
                            const u = users.find(u => u._id === emp);
                            if (!u) return '—';
                            const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
                            return full || u.email || '—';
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        {exception.isRecurring ? (
                          <Box>
                            <Chip
                              label="Da"
                              size="small"
                              color="secondary"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" sx={commonStyles.textSecondary}>
                              {exception.recurringDays.map(day => getDayName(day)).join(", ")}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip
                            label="Ne"
                            size="small"
                            color="default"
                            sx={{
                              background: colors.status.inactive.background,
                              border: colors.status.inactive.border,
                              color: colors.status.inactive.light,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={commonStyles.textPrimary}>
                        {exception.createdBy ? 
                          `${exception.createdBy.firstName || ''} ${exception.createdBy.lastName || ''}`.trim() || 
                          exception.createdBy.email || 
                          'Nepoznato' 
                          : 'Nepoznato'
                        }
                      </TableCell>
                      <TableCell sx={commonStyles.textPrimary}>
                        {exception.createdAt ? formatDate(exception.createdAt) : 'Nepoznato'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(exception)}
                          sx={commonStyles.iconButton(customColors.primary.main)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(exception._id)}
                          sx={commonStyles.iconButton(colors.actions.delete)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Paginacija - samo za desktop */}
            <Pagination
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Redova po stranici:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
              }
            />
          </div>
        )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: commonStyles.dialogPaper
        }}
      >
                 <DialogTitle sx={commonStyles.dialogTitle}>
           {editingException ? "Uredi neradno vreme" : "Dodaj novo neradno vreme"}
           {isSuperAdmin(currentUser) && selectedTenantId && (
             <Typography variant="subtitle2" sx={{ mt: 1, ...commonStyles.textSecondary }}>
               Klijent: {tenants.find(t => t._id === (typeof selectedTenantId === 'object' ? selectedTenantId._id : selectedTenantId))?.name}
             </Typography>
           )}
         </DialogTitle>
        <DialogContent sx={{ ...commonStyles.dialogContent, pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Naslov"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={{ ...commonStyles.formField, mb: 2 }}
            />

            <TextField
              fullWidth
              label="Opis"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              sx={{ ...commonStyles.formField, mb: 2 }}
            />

            {/* Switch za neradno vreme za određenog zaposlenog - sakrij ako je samo jedan zaposleni */}
            {users.length > 1 && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.forSpecificEmployee}
                      onChange={(e) => setFormData({ ...formData, forSpecificEmployee: e.target.checked, employeeId: e.target.checked ? formData.employeeId : '' })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: customColors.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: `${customColors.primary.main}80`,
                        },
                      }}
                    />
                  }
                  label="Za određenog zaposlenog"
                  sx={{ ...commonStyles.textPrimary, mb: 2 }}
                />

                {formData.forSpecificEmployee && (
                  <FormControl fullWidth sx={{ ...commonStyles.formField, mb: 2 }}>
                    <InputLabel sx={commonStyles.textSecondary}>Zaposleni</InputLabel>
                    <Select
                      value={formData.employeeId}
                      label="Zaposleni"
                      onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                      sx={commonStyles.selectField}
                      MenuProps={{
                        PaperProps: { sx: commonStyles.selectMenu }
                      }}
                    >
                      <MenuItem value="">
                        <em>Izaberite zaposlenog</em>
                      </MenuItem>
                      {users.map((u) => {
                        const userActualType = getUserTypeFromConnections(u, selectedTenantId) || u.userType;
                        const isSuperAdminUser = isSuperAdmin(u);
                        return (
                          <MenuItem key={u._id} value={u._id}>
                            {`${u.firstName || ''} ${u.lastName || ''}`.trim()} {userActualType === 'tenant_admin' ? '(Admin)' : isSuperAdminUser ? '(Super Admin)' : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              </>
            )}

                         <FormControl fullWidth sx={{ mb: 2 }}>
               <InputLabel sx={commonStyles.textSecondary}>Tip neradnog vremena</InputLabel>
               <Select
                 value={formData.type}
                 label="Tip neradnog vremena"
                onChange={(e) => {
                  const newType = e.target.value;
                  setFormData({ 
                    ...formData, 
                    type: newType,
                    // Za "all_days_specific_hours" automatski postavi isRecurring na true
                    // Resetuj isRecurring za tipove koji ne podržavaju ponavljanje
                    isRecurring: newType === "all_days_specific_hours" ? true : (newType === "one_day" || newType === "specific_hours_one_day") ? false : formData.isRecurring,
                    // Resetuj datume za all_days_specific_hours
                    startDate: newType === "all_days_specific_hours" ? "" : formData.startDate,
                    endDate: newType === "all_days_specific_hours" ? "" : formData.endDate,
                  });
                }}
                sx={commonStyles.selectField}
                MenuProps={{
                  PaperProps: {
                    sx: commonStyles.selectMenu
                  }
                }}
              >
                <MenuItem value="one_day">Jedan dan</MenuItem>
                <MenuItem value="multiple_days">Više dana</MenuItem>
                <MenuItem value="specific_hours_one_day">Specifični sati - jedan dan</MenuItem>
                <MenuItem value="specific_hours_multiple_days">Specifični sati - više dana</MenuItem>
                <MenuItem value="all_days_specific_hours">Pauza - svi dani</MenuItem>
              </Select>
            </FormControl>

            {formData.type !== "all_days_specific_hours" && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Datum početka"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={commonStyles.formField}
                  />
                </Grid>
                {(formData.type === "multiple_days" || formData.type === "specific_hours_multiple_days") && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Datum kraja"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={commonStyles.formField}
                    />
                  </Grid>
                )}
              </Grid>
            )}

                         {(formData.type === "specific_hours_one_day" || formData.type === "specific_hours_multiple_days" || formData.type === "all_days_specific_hours") && (
               <Grid container spacing={2} sx={{ mb: 2 }}>
                 <Grid item xs={6}>
                   <FormControl fullWidth sx={commonStyles.formField}>
                     <InputLabel sx={commonStyles.textSecondary}>Vreme početka</InputLabel>
                     <Select
                       value={formData.startTime}
                       label="Vreme početka"
                       onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                       sx={commonStyles.selectField}
                       MenuProps={{
                         PaperProps: {
                           sx: commonStyles.selectMenu
                         }
                       }}
                     >
                       {generateStartTimeSlots().map((time) => (
                         <MenuItem key={time} value={time}>
                           {time}
                         </MenuItem>
                       ))}
                     </Select>
                   </FormControl>
                 </Grid>
                 <Grid item xs={6}>
                   <FormControl fullWidth sx={commonStyles.formField}>
                     <InputLabel sx={commonStyles.textSecondary}>Vreme kraja</InputLabel>
                     <Select
                       value={formData.endTime}
                       label="Vreme kraja"
                       onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                       sx={commonStyles.selectField}
                       MenuProps={{
                         PaperProps: {
                           sx: commonStyles.selectMenu
                         }
                       }}
                     >
                       {generateEndTimeSlots().map((time) => (
                         <MenuItem key={time} value={time}>
                           {time}
                         </MenuItem>
                       ))}
                     </Select>
                   </FormControl>
                 </Grid>
               </Grid>
             )}

            {(formData.type === "multiple_days" || formData.type === "specific_hours_multiple_days") && formData.type !== "all_days_specific_hours" && (
              <FormControlLabel
                control={
                  <Radio
                    checked={formData.isRecurring}
                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                    sx={{
                      color: customColors.primary.main,
                      '&.Mui-checked': {
                        color: customColors.primary.main,
                      },
                    }}
                  />
                }
                label="Specifični dani"
                sx={commonStyles.textPrimary}
              />
            )}

            {(formData.isRecurring || formData.type === "all_days_specific_hours") && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, ...commonStyles.textPrimary }}>
                  Dani u nedelji:
                </Typography>
                <Grid container spacing={1}>
                  {(() => {
                    // Koristi currentTenant, ako nema onda koristi helper funkciju
                    const tenantId = getCurrentTenantId();
                    const tenantData = currentTenant || (tenantId ? { tenantId } : null);
                    const workingHours = tenantData?.workingHours;
                    
                    const allDays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
                    
                    // Filtriraj samo dane kada je tenant otvoren
                    const availableDays = workingHours 
                      ? allDays.filter(day => workingHours[day]?.isOpen)
                      : allDays; // Fallback na sve dane ako nema workingHours
                    
                    return availableDays.map((day) => (
                      <Grid item xs={6} sm={3} key={day}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={formData.recurringDays.includes(day)}
                              onChange={() => handleRecurringDayChange(day)}
                              sx={{
                                color: customColors.primary.main,
                                '&.Mui-checked': {
                                  color: customColors.primary.main,
                                },
                              }}
                            />
                          }
                          label={getDayName(day)}
                          sx={commonStyles.textSecondary}
                        />
                      </Grid>
                    ));
                  })()}
                </Grid>
                
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={commonStyles.dialogActions}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={commonStyles.cancelButton}>
            Otkaži
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={commonStyles.gradientButton}
            disabled={submitting}
          >
            {editingException ? "Ažuriraj" : "Kreiraj"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog za potvrdu konflikata */}
      <Dialog
        open={showConflictDialog}
        onClose={() => setShowConflictDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { ...commonStyles.dialogPaper },
        }}
      >
        <DialogTitle sx={{...commonStyles.dialogTitle, pb: 1}}>
          <Typography variant="h6" sx={commonStyles.textPrimary}>
            Potvrda kreiranja neradnog vremena
          </Typography>
        </DialogTitle>
        <DialogContent sx={commonStyles.dialogContent}>
          {conflictData && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Postoje termini koji će biti pogođeni:
                </Typography>
                <Typography variant="body2">
                  • Ukupno: {conflictData.totalConflicts}
                </Typography>
                <Typography variant="body2">
                  • Na čekanju: {conflictData.pendingCount}
                </Typography>
                <Typography variant="body2">
                  • Potvrđeni: {conflictData.confirmedCount}
                </Typography>
              </Alert>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                Da li želite da ih otkažete i kreirate neradno vreme?
              </Typography>
              
              {conflictData.conflictingAppointments && conflictData.conflictingAppointments.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Detalji konfliktnih termina:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {conflictData.conflictingAppointments.map((appointment, index) => (
                      <Box key={appointment._id} sx={{ 
                        p: 1, 
                        mb: 1, 
                        border: `1px solid ${customColors.border.light}`,
                        borderRadius: 1,
                        backgroundColor: colors.ui.background.dark
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {appointment.customerName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: customColors.text.secondary }}>
                          {new Date(appointment.appointmentDate).toLocaleDateString('sr-RS')} - {appointment.conflictTime}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: appointment.status === 'confirmed' ? colors.status.appointment.confirmed : colors.status.appointment.pending,
                          ml: 1
                        }}>
                          ({appointment.status === 'confirmed' ? 'Potvrđeno' : 'Na čekanju'})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={commonStyles.dialogActions}>
          <Button 
            onClick={() => setShowConflictDialog(false)} 
            variant="outlined"
            sx={commonStyles.cancelButton}
          >
            Otkaži
          </Button>
          <Button 
            onClick={createWithConfirmation} 
            variant="contained" 
            sx={commonStyles.gradientButton}
          >
            Potvrdi i otkaži termine
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Exceptions;
